/**
 * HTTP client for the Worker admin users surface.
 *
 * Mirrors `admin/api.ts` + `admin/signups-api.ts`:
 *   - cookie-credentialed (the session cookie is the load-bearing auth
 *     token; the worker's `requireSystemAdmin` returns **404** for
 *     non-admins so the route's existence is not leaked).
 *   - resolves to a discriminated `AdminResult<T>` so the view can branch
 *     on `not-found` / `unauthenticated` / `error`.
 */
import { createAdminRequest, type AdminResult } from './admin-fetch.js';

export type { AdminResult } from './admin-fetch.js';

const request = createAdminRequest('/v1/admin/users');

export interface UserSummary {
  id: string;
  email: string;
  created_at: string;
  email_verified: boolean;
  is_system_admin: boolean;
  totp_enabled: boolean;
  backup_codes_remaining: number;
  has_recovery_email: boolean;
  pending_deletion: boolean;
  account_name: string | null;
  campaign_count: number;
  active_session_count: number;
}

export interface UserDetailMembership {
  campaign_id: string;
  campaign_name: string | null;
  role: string;
  added_at: string;
}

export interface UserDetailCampaign {
  id: string;
  name: string;
  created_at: string;
}

export interface UserDetailSession {
  id: string;
  created_at: string;
  last_seen_at: string;
  expires_at: string;
  revoked_at: string | null;
  user_agent: string | null;
  active: boolean;
}

export interface UserDetailEmail {
  template: string;
  sent_at: string;
  outcome: string;
}

export interface UserDetail {
  user: {
    id: string;
    email: string;
    created_at: string;
    email_verified_at: string | null;
    is_system_admin: boolean;
    totp_enabled_at: string | null;
    backup_codes_remaining: number;
    pending_deletion_at: string | null;
    recovery_email: string | null;
  };
  account: { id: string; name: string; created_at: string } | null;
  memberships: UserDetailMembership[];
  owned_campaigns: UserDetailCampaign[];
  sessions: UserDetailSession[];
  emails: UserDetailEmail[];
}

export interface UsersPage {
  users: UserSummary[];
  total: number;
  limit: number;
  offset: number;
}

export interface ListUsersParams {
  q?: string;
  limit?: number;
  offset?: number;
}

export function listUsers(params: ListUsersParams = {}): Promise<AdminResult<UsersPage>> {
  const qs = new URLSearchParams();
  if (params.q) qs.set('q', params.q);
  if (params.limit !== undefined) qs.set('limit', String(params.limit));
  if (params.offset !== undefined) qs.set('offset', String(params.offset));
  const suffix = qs.toString();
  // No trailing slash before the query: a mounted Hono sub-app's `GET /`
  // matches `/v1/admin/users` but NOT `/v1/admin/users/` (trailing slash
  // misses the route and collapses to the gate's 404).
  return request(suffix ? `?${suffix}` : '');
}

export function getUser(id: string): Promise<AdminResult<UserDetail>> {
  return request(`/${encodeURIComponent(id)}`);
}

// ─────────────────────────────────────────────────────────── actions ─

const ENC = encodeURIComponent;

/** Grant / revoke the `is_system_admin` flag. 409 `last_admin` if it would
 * remove the final administrator. */
export function setSystemAdmin(
  id: string,
  isSystemAdmin: boolean,
): Promise<AdminResult<{ ok: true; is_system_admin: boolean; changed: boolean }>> {
  return request(`/${ENC(id)}/system-admin`, {
    method: 'POST',
    body: JSON.stringify({ is_system_admin: isSystemAdmin }),
  });
}

/** Force sign-out — revoke every active session. */
export function revokeSessions(
  id: string,
): Promise<AdminResult<{ ok: true; revoked: number }>> {
  return request(`/${ENC(id)}/revoke-sessions`, { method: 'POST', body: '{}' });
}

/** Send a password-reset email to the user. */
export function triggerPasswordReset(
  id: string,
): Promise<AdminResult<{ ok: true; outcome: string }>> {
  return request(`/${ENC(id)}/password-reset`, { method: 'POST', body: '{}' });
}

/** Resend the verification email (409 `already_verified` if not pending). */
export function resendVerification(
  id: string,
): Promise<AdminResult<{ ok: true; outcome: string }>> {
  return request(`/${ENC(id)}/resend-verification`, { method: 'POST', body: '{}' });
}

/** Cancel a pending account deletion. */
export function cancelDeletion(
  id: string,
): Promise<AdminResult<{ ok: true; changed: boolean }>> {
  return request(`/${ENC(id)}/cancel-deletion`, { method: 'POST', body: '{}' });
}
